/* eslint-disable react-hooks/rules-of-hooks */
import { ConnectForm, ControlledComponentProps, ErrorMessages, Icon, Label, PillGroup } from "@/components"
import { useStyles } from "@/components/Controlled/AddTextItemsToListInput/AddTextItemsToListInput.styles"
import { Button as MantineButton, TextInput as MantineTextInput, useMantineTheme } from "@mantine/core"
import React from "react"
import { FieldValues, RegisterOptions, useController, useFormContext } from "react-hook-form"

interface AddTextItemsToListInputProps {
    name: string
    label: string
    rules?: Omit<RegisterOptions<FieldValues, string>, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled">
    filters: { [key: string]: (value) => boolean }
    placeholder?: string
    maxItems: number
    icon?: React.ReactNode
    tooltip?: string
    defaultValue?: string
    shouldUnregister?: boolean
    transform?: { [key: string]: (value: string) => string }
}

const AddTextItemsToListInput = ({
    name,
    label,
    rules,
    filters,
    placeholder,
    tooltip,
    defaultValue,
    shouldUnregister,
    maxItems = 10,
    transform,
    ...props
}: ControlledComponentProps<AddTextItemsToListInputProps>) => (
    <ConnectForm>
        {({ control, clearErrors }) => {
            const {
                field,
                fieldState: { error },
            } = useController({
                name,
                control,
                defaultValue,
                shouldUnregister,
                rules,
            })
            const [isInputFocus, setIsInputFocus] = React.useState(false)
            const [isValid, setIsValid] = React.useState(true)
            const [value, setValue] = React.useState("")
            const { trigger } = useFormContext()
            const { classes } = useStyles({ isInputFocus })
            const theme = useMantineTheme()

            const isLimitReached = React.useCallback(() => {
                const size = field.value?.length ?? 0
                return size >= maxItems
            }, [field.value])

            const handleAddClick = (
                event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>,
            ) => {
                event.preventDefault()
                if (isValid && !isLimitReached()) {
                    const uniqueEmailsList = new Set(field.value)
                    field.onChange(Array.from(uniqueEmailsList.add(value)))
                    setValue("")
                    clearErrors(name)
                }
            }

            const triggerValidation = () => {
                if (!isValid) {
                    trigger(name)
                }
            }

            const handleOnChange = (event) => {
                setValue(transform?.output(event.target.value) ?? event.target.value)
                setIsValid(filters?.inputValidate(event.target.value))
                isLimitReached()
            }

            const asteriskStatus = rules?.required || false

            return (
                <>
                    <div className={classes.container}>
                        <Label
                            label={label}
                            tooltip={tooltip}
                            withAsterisk={!!asteriskStatus}
                            className={classes.label}
                        />
                        <div className={classes.input}>
                            <MantineTextInput
                                {...props}
                                {...field}
                                aria-invalid={Boolean(error)}
                                placeholder={placeholder}
                                id={name}
                                icon={<Icon icon="envelope-open" iconFamily="far" color={theme.colors.violet[5]} />}
                                onChange={handleOnChange}
                                value={value}
                                onKeyDown={(event) => {
                                    if (isValid && event.keyCode === 13) handleAddClick(event)
                                }}
                                onFocus={() => setIsInputFocus(true)}
                                onBlur={() => {
                                    triggerValidation()
                                    setIsInputFocus(false)
                                }}
                            />
                            <MantineButton
                                className={classes.button}
                                disabled={!(isValid && !isLimitReached())}
                                onClick={handleAddClick}
                            >
                                <Icon icon="plus" iconFamily="far" size="1x" />
                            </MantineButton>
                        </div>
                        {!isValid && !isLimitReached() && (
                            <ErrorMessages fieldError={{ type: "custom", message: "Invalid email" }} />
                        )}
                        {isLimitReached() && value?.length > 0 && (
                            <ErrorMessages fieldError={{ type: "custom", message: "Email limit reached" }} />
                        )}
                        <PillGroup data={field.value} onClose={field.onChange} />
                        {error && <ErrorMessages fieldError={error} />}
                    </div>
                </>
            )
        }}
    </ConnectForm>
)

export default AddTextItemsToListInput
